import { HTTPError, TimeoutError } from 'ky';

/**
 * RFC 7807 ProblemDetails — formato que o backend .NET sempre devolve
 * em erros (graças ao `AddProblemDetails` no Program.cs).
 *
 * O backend customiza com:
 *   - `code`: enum estável (ex: INVALID_CREDENTIALS) — usado pra branch UI;
 *   - `traceId`: correlation pra logs;
 *   - `errors`: dicionário de campo→mensagens (em ValidationProblem).
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
}

/**
 * Shape limpo que componentes consomem — código padronizado + mensagem
 * pronta + map de erros por campo (vazio quando não há).
 */
export interface ParsedApiError {
  readonly status: number;
  readonly code: string;
  readonly detail: string;
  readonly fieldErrors: Record<string, string[]>;
  readonly traceId?: string;
}

/**
 * Normaliza qualquer erro (HTTPError do ky, TimeoutError, exception JS
 * genérica) em <see cref="ParsedApiError"/>. Sempre retorna algo legível —
 * nunca propaga undefined/null pro template.
 */
export async function parseProblemDetails(err: unknown): Promise<ParsedApiError> {
  // ky.HTTPError tem response — tenta extrair body como ProblemDetails.
  if (err instanceof HTTPError) {
    let body: ProblemDetails = {};
    try {
      body = (await err.response.clone().json()) as ProblemDetails;
    } catch {
      // body não-JSON (ex: HTML de error page do servidor). Mantém body vazio.
    }
    return {
      status: err.response.status,
      code: body.code ?? `HTTP_${err.response.status}`,
      detail: body.detail ?? body.title ?? err.message,
      fieldErrors: body.errors ?? {},
      ...(body.traceId !== undefined && { traceId: body.traceId }),
    };
  }

  if (err instanceof TimeoutError) {
    return {
      status: 0,
      code: 'NETWORK_TIMEOUT',
      detail: 'O servidor demorou demais pra responder. Tente novamente.',
      fieldErrors: {},
    };
  }

  if (err instanceof Error) {
    return {
      status: 0,
      code: 'NETWORK_ERROR',
      detail: err.message || 'Falha de rede inesperada.',
      fieldErrors: {},
    };
  }

  return {
    status: 0,
    code: 'UNKNOWN_ERROR',
    detail: 'Erro inesperado — abra o console pra detalhes.',
    fieldErrors: {},
  };
}
